import { useState } from 'react';
import {
  getDataIndexedColumns,
  getDefaultTablePagination,
  useTableSearchParams,
} from 'shared/UI/UIKit/Table';
import { useFilteredColumns } from 'common/ColumnsFilter/use-filtered-columns';
import { useGetEducationClassListQuery, useLazyGetTeacherWorkloadsQuery } from 'shared/api';
import type { AppTableState } from 'shared/UI/UIKit/Table';
import type { TeacherWorkload, TeacherWorkloadsRequest } from 'shared/api';
import type { TeacherWorkloadsTableRow } from './types';
import { getColumns } from '../config';
import { toTeacherWorkloadsTableRows } from './utils';

interface UseTableServiceProps {
  onEdit: (record: TeacherWorkload) => void;
  onDelete: (record: TeacherWorkload) => void;
  onCopy: (record: TeacherWorkload) => void;
}

const TEACHER_WORKLOADS_PAGE_SIZE_OPTIONS = ['5', '10', '20', '50', '100'] as const;

export const useTableService = ({ onEdit, onDelete, onCopy }: UseTableServiceProps) => {
  const [loading, setLoading] = useState(false);
  const [teacherWorkloads, setTeacherWorkloads] = useState<TeacherWorkload[]>([]);
  const [teacherWorkloadTableRows, setTeacherWorkloadTableRows] = useState<
    TeacherWorkloadsTableRow[]
  >([]);
  const [total, setTotal] = useState(0);
  const { data: educationClasses } = useGetEducationClassListQuery({ size: 1000 });
  const [fetchTeacherWorkloads] = useLazyGetTeacherWorkloadsQuery();

  const getTableItems = async (
    nextTableState: AppTableState,
    nextFilterState: Record<string, any>,
  ) => {
    const {
      pagination: { current, pageSize },
      sorters,
    } = nextTableState;
    const sort = sorters
      .filter((col) => !!col.order)
      .map((col) => `${col.dataIndex.join('.')},${col.order === 'ascend' ? 'asc' : 'desc'}`);

    const apiParams: TeacherWorkloadsRequest = {
      page: current! - 1,
      size: pageSize ?? 10,
      sort: sort.length > 0 ? sort : undefined,
      ...nextFilterState,
    };

    setLoading(true);

    try {
      const res = await fetchTeacherWorkloads(apiParams).unwrap();
      setTeacherWorkloads(res.content);
      setTeacherWorkloadTableRows(toTeacherWorkloadsTableRows(res.content));
      setTotal(res.totalElements);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = getColumns({
    onEdit,
    onDelete,
    onCopy,
    educationClasses: educationClasses?.content || [],
  });

  const { filteredColumns, hiddenColumns, visibleKeys, setVisibleKeys } =
    useFilteredColumns(columns);

  const initialTableState: AppTableState = {
    sorters: getDataIndexedColumns(columns)
      .filter(({ key }) => !['controls', 'count'].includes(key))
      .map(({ key, dataIndex }) => {
        return key === 'id'
          ? {
              key,
              dataIndex,
              order: 'descend',
            }
          : {
              key,
              dataIndex,
            };
      }),
    filters: [],
    pagination: {
      ...getDefaultTablePagination(),
      pageSize: 5,
      pageSizeOptions: [...TEACHER_WORKLOADS_PAGE_SIZE_OPTIONS],
    },
  };

  const {
    tableState,
    onTableChange,
    filterState,
    filterCount,
    onFilterChange,
    onItemRemove,
    refreshPage,
  } = useTableSearchParams(
    initialTableState,
    getTableItems,
    teacherWorkloads.length,
    total,
    hiddenColumns,
  );

  const pageSize = tableState.pagination.pageSize ?? 5;

  const onPaginationChange = (page: number, nextPageSize?: number) => {
    const limit = nextPageSize ?? pageSize;

    onTableChange(
      {
        ...tableState,
        pagination: {
          ...tableState.pagination,
          current: page,
          pageSize: limit,
        },
      },
      {
        sort: null,
        filter: null,
        page: page - 1,
        limit,
      },
      'paginate',
    );
  };

  // const onRow: AppTableProps<TeacherWorkloadsTableRow>['onRow'] = (record) => {
  //   return {
  //     onClick: () => {
  //       navigate(
  //         getTeacherWorkloadUrl({ teacherWorkloadId: record.teacherWorkload.id.toString() }),
  //       );
  //     },
  //   };
  // };

  return {
    state: {
      teacherWorkloads: teacherWorkloadTableRows,
      loading,
      columns,
      filteredColumns,
      hiddenColumns,
      visibleKeys,
      tableState,
      filterState,
      filterCount,
      pageSize,
      pageSizeOptions: [...TEACHER_WORKLOADS_PAGE_SIZE_OPTIONS],
    },
    methods: {
      refreshPage,
      setVisibleKeys,
      onItemRemove,
      onFilterChange,
      onTableChange,
      onPaginationChange,
    },
  };
};
